/**
 * Générateur — Automatisme : tester si un nombre vérifie une égalité.
 * Attendu ID : 1111 | Niveau : 4ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 10, ligne 711 :
 * « Tester si un nombre vérifie une égalité. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Version mentale de l'objectif 5e l. 610 (gen_934), socle de l'objectif 4e
 * « tester si un nombre est solution d'une équation » (gen_162, mise en
 * œuvre p. 18). Nombre DONNÉ, égalité à UN membre littéral simple (ax + b,
 * b + ax, ax, x + b) et un nombre, réponse oui/non : l'élève remplace et
 * compare, il ne résout pas (« un test d'égalité permet de distinguer les
 * statuts du signe = », mise en œuvre p. 13). Deux membres littéraux, x²,
 * fractions, tableau = 162 ; croisement = 934 ; puissances = 931 ; « quel
 * nombre vérifie… » (résoudre par essais) = le QCM de 162 : exclus (À
 * ARBITRER). x négatif exclu par défaut (3 × (−2) = objectif l. 640).
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5NE4 « clavier
 * V/F », quatre options à deux membres, sur-dimensionnées : le mécanisme
 * (vraie par construction, fausse par une autre valeur) est repris, la
 * forme réduite à un membre.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Vrai ou faux » (test_tableau_vf) : quatre lignes « Pour
 *     x = 4 : 3x + 2 = 14 », deux vraies, deux fausses dont une par famille
 *     (« 2 + 3x = 20 » gauche_a_droite ; « 3x + 2 = 36 » concatenation ;
 *     « = 9 » x_ignore ; « = 15 » voisin). C'est la série flash de l'attendu.
 *   Carte 2 bis « 3x, c'est… » (substitution_qcm_unique, venue de 1108 par
 *     arbitrage prof 2026-09-12) : « 3x, pour x = 8, c'est… », QUATRE choix
 *     24 (clé), 38 (concatenation, DEPP q.17 15,4 %), 11 (lecture_additive),
 *     512 (puissance) ; le premier geste du test, remplacer et calculer.
 *   Carte 2 « Laquelle est vraie ? » (vraie_qcm_unique) : « Pour x = 4, une
 *     seule égalité est vraie. Laquelle ? » QUATRE égalités : 3x + 2 = 14
 *     (clé), = 20 (gauche_a_droite, LA principale : DEPP 4e 2023 q.17,
 *     22,6 %), = 36 (concatenation, 15,4 %), = 9 (x_ignore) : 9 < 14 < 20 <
 *     36 ; avec 3x − 2 (10 ; 4 ; 30 ; 5) la clé bouge.
 *   Carte 3 « Je remplace, je calcule, je compare » (geste_texte_trou_drag) :
 *     « Pour x = 4, est-ce que 3x + 2 = 14 ? 3 × {{4}} + 2 = {{14}}. Réponse :
 *     {{oui}} » ; leurres : l'autre réponse, la valeur collée (36), le
 *     membre droit quand il diffère. Le geste en entier.
 *
 * INVARIANTS : ax + b et b + ax (a 2..5, b 1..9, + ou −), ax, x + b ;
 * membre droit entier ; x entier 0..6 ; un cas vrai sur deux ; le faux est
 * une valeur de famille (gauche_a_droite, concatenation, x_ignore) ou à
 * ± 1..3 de la clé ; tout re-calculé (throw).
 * SACRIFIÉ (fiche d'aide à venir, « 3 × 4 + 2 = 14, 14 = 14 : oui », qui
 * dit la même chose que 1106 et 1108) : (3x + 2 ; x = 4) quel que soit le
 * membre droit, auto-testé.
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   gauche_a_droite : 2 + 3x pour x = 4 → (2 + 3) × 4 = 20 (DEPP q.17) ;
 *   concatenation   : 3x lu 34, 34 + 2 = 36 (q.17) ;
 *   x_ignore        : 3 + 2 = 5, le x oublié ;
 *   membre_seul     : le membre gauche calculé, la comparaison oubliée
 *                     (drag : « oui » quel que soit le membre droit) ;
 *   voisin          : à ± 1..3 de la clé (V/F).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 1111;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_VF = 'Vrai ou faux';
const L_QCM = 'Laquelle est vraie ?';
const L_SUBST = '3x, c\'est…';
const L_DRAG = 'Je remplace, je calcule, je compare';

const FOIS = '×';
const LETTRES = ['x', 'x', 'x', 'n', 't'];

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple de la fiche : 3x + 2 pour x = 4, sacrifié quel que soit le membre droit. */
export const SACRIFIE = { a: 3, b: 2, signe: '+', x: 4 };

/**
 * Une expression { forme, a, b, signe } : 'ax_b' (ax + b, ax − b), 'b_ax'
 * (b + ax), 'ax', 'x_b' (x + b). Valeur pour x = v : le second chemin.
 */
const valeur = (e, v) => {
    if (e.forme === 'ax') return e.a * v;
    if (e.forme === 'x_b') return v + e.b;
    return e.a * v + (e.signe === '+' ? e.b : -e.b);
};
const controle = (e, v) => {
    const produit = e.forme === 'x_b' ? v : Array(e.a).fill(v).reduce((s, t) => s + t, 0);
    if (e.forme === 'ax') return produit;
    return e.signe === '+' ? produit + e.b : produit - e.b;
};
const ecriture = (e, L) => {
    if (e.forme === 'ax') return `${e.a}${L}`;
    if (e.forme === 'x_b') return `${L} + ${e.b}`;
    if (e.forme === 'b_ax') return `${e.b} + ${e.a}${L}`;
    return `${e.a}${L} ${e.signe === '+' ? '+' : '-'} ${e.b}`;
};
const estSacrifie = (e, x) => e.a === SACRIFIE.a && e.b === SACRIFIE.b && e.signe === SACRIFIE.signe && x === SACRIFIE.x && (e.forme === 'ax_b' || e.forme === 'b_ax');

/** Un item { e, x, v } : expression, nombre testé, valeur ; assert. */
const item = (e, x) => {
    const v = valeur(e, x);
    if (!Number.isInteger(v) || v < 0 || v !== controle(e, x)) throw new Error(`gen_1111 : ${JSON.stringify(e)} pour ${x}`);
    if (estSacrifie(e, x)) throw new Error('gen_1111 : item sacrifié');
    return { e, x, v };
};
const tirer = (formes = ['ax_b', 'ax_b', 'b_ax', 'ax', 'x_b']) => {
    for (let essai = 0; essai < 80; essai++) {
        const forme = randomChoice(formes);
        const e = { forme, a: randInt(2, 5), b: randInt(1, 9), signe: forme === 'ax_b' ? randomChoice(['+', '-']) : '+' };
        const x = randInt(0, 6);
        if (valeur(e, x) < 0 || estSacrifie(e, x)) continue;
        if (forme === 'x_b' && x === 0) continue; // x + 7 pour x = 0 : rien à remplacer
        return item(e, x);
    }
    return item({ forme: 'ax_b', a: 2, b: 5, signe: '+' }, 3);
};

/** Les valeurs fausses de famille pour un item ; celles qui coïncident avec la clé sont écartées. */
const famillesFausses = ({ e, x, v }) => {
    const c = [];
    if (e.forme === 'b_ax') c.push({ valeur: (e.b + e.a) * x, code: 'gauche_a_droite' });
    if (e.forme !== 'x_b') c.push({ valeur: Number(`${e.a}${x}`) + (e.forme === 'ax' ? 0 : (e.signe === '+' ? e.b : -e.b)), code: 'concatenation' });
    if (e.forme !== 'ax' && e.forme !== 'x_b') c.push({ valeur: e.a + (e.signe === '+' ? e.b : -e.b), code: 'x_ignore' });
    if (e.forme === 'x_b') c.push({ valeur: Number(`${x}${e.b}`), code: 'concatenation' });
    return c.filter((f) => Number.isInteger(f.valeur) && f.valeur >= 0 && f.valeur !== v);
};

const EXPLICATIONS = {
    gauche_a_droite: 'On ne lit pas de gauche à droite : 2 + 3x, c\'est 2 plus 3 × x. Pour x = 4 : 2 + 12 = 14.',
    concatenation: '3x n\'est pas 34 : c\'est 3 × 4 = 12, puis 12 + 2 = 14.',
    lecture_additive: '3x, c\'est 3 × x, pas 3 + x.',
    puissance: '3x, c\'est trois fois x, pas x × x × x.',
    carre: '3x, c\'est trois fois x, pas x × x.',
    x_ignore: 'Il faut remplacer x par le nombre avant de calculer.',
    membre_seul: 'Calculer le membre de gauche ne suffit pas : je compare au membre de droite.',
    voisin: 'Je refais le calcul : 3 × 4 = 12, 12 + 2 = 14.',
    regle: 'Je remplace la lettre par le nombre, je calcule, je compare au membre de droite : égal, c\'est oui ; sinon, c\'est non.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille (catalogue validé) ; null : voisin (tiré au hasard) et carre (x² pour ax, un substitut), jamais rangés. x_ignore, lecture_additive et puissance ont leurs propres codes depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : l'erreur se fait en remplaçant x. */
const CODE_COMMUN = {
    gauche_a_droite: 'gauche_a_droite', concatenation: 'concatenation', x_ignore: 'lettre_oubliee_en_remplacant', lecture_additive: 'substitution_en_somme',
    puissance: 'substitution_en_puissance', membre_seul: 'comparaison_oubliee', carre: null, voisin: null,
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre, son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } (code null : non rangé) : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** Le code commun d'une famille. Relecture du 04/10/2026 (E21) : dans x + b (« pour t = 2 : t + 9 = 29 »),
 * aucun × sous-entendu n'est oublié, c'est le + écrit qui disparaît : une autre erreur, sans nom. */
const codeDe = (f, it) => (f.code === 'concatenation' && it.e.forme === 'x_b' ? null : CODE_COMMUN[f.code]);

/** Les valeurs fausses nommables d'un item : ses familles, sauf une valeur que deux familles donnent (2 + 3x pour x = 1). */
const nommablesItem = (it) => nommer(famillesFausses(it).map((f) => ({ code: codeDe(f, it), valeur: f.valeur })), it.v);

/** Glisser-déposer : le oui ou le non contraire posé à la réponse (trou 2) est la comparaison oubliée ; un nombre faux posé à x ou au calcul reste sans code. */
const erreursDrag = (leurres) => leurres.filter((valeur) => /^(oui|non)$/.test(valeur))
    .map((valeur) => ({ code: CODE_COMMUN.membre_seul, trou: 2, valeur }));

/** Vrai/faux : chaque égalité fausse de famille dont le membre de droite est une valeur nommable de son item. */
const erreursVf = (items, lignes) => lignes.flatMap((l, i) => {
    const droite = (l.ligne.match(/ = (\d+)\$$/) || [])[1];
    const e = CODE_COMMUN[l.code] && nommablesItem(items[i]).find((x) => x.code === CODE_COMMUN[l.code] && cleReponse(x.valeur) === cleReponse(droite));
    return e ? [{ code: e.code, ligne: l.ligne }] : [];
});

// ─── Carte 1 : Vrai ou faux (la série flash de l'attendu) ─────────────────

// Ligne entière en KaTeX (règle prof 2026-09-12 : la lettre en mode maths partout).
const ligneVf = (it, L, droite) => tex(`\\text{Pour } ${L} = ${it.x} \\text{ : } ${ecriture(it.e, L)} = ${droite}`);

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const items = [tirer(), tirer(), tirer(), tirer()];
        if (new Set(items.map((it) => ecriture(it.e, L))).size < 4) continue;
        const verites = shuffle([true, true, false, false]);
        const lignes = items.map((it, i) => {
            if (verites[i]) return { ligne: ligneVf(it, L, it.v), code: null };
            const familles = famillesFausses(it);
            const choix = familles.length && randomChoice([true, false])
                ? randomChoice(familles)
                : { valeur: it.v + randomChoice([-3, -2, -1, 1, 2, 3]), code: 'voisin' };
            if (choix.valeur < 0) return null;
            return { ligne: ligneVf(it, L, choix.valeur), code: choix.code };
        });
        if (lignes.some((l) => !l)) continue;
        // au moins une fausse de famille (pas seulement des voisins)
        if (!lignes.some((l) => l.code && l.code !== 'voisin')) continue;
        lignes.forEach((l, i) => {
            const m = l.ligne.replace(/[$]/g, '').match(/ = (\d+)$/);
            if ((Number(m[1]) === items[i].v) !== verites[i]) throw new Error(`gen_1111 : ligne mal étiquetée ${l.ligne}`);
        });
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursVf(items, lignes));
        payload._meta.params = { lettre: L, items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const lignes = ['2x + 5 = 11', '4x = 20', '5 + 2x = 14', 'x + 3 = 8'].map((e, i) => tex(`\\text{Pour } x = ${[3, 5, 2, 6][i]} \\text{ : } ${e}`));
    const payload = makeVraiFaux(lignes, [0, 0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursVf([null, null, { e: { forme: 'b_ax', a: 2, b: 5, signe: '+' }, x: 2, v: 9 }, null], lignes.map((ligne, i) => ({ ligne, code: i === 2 ? 'gauche_a_droite' : null }))));
    payload._meta.params = { lettre: 'x', verites: [true, true, false, false], distracteurs: [{ code: 'gauche_a_droite' }, { code: 'voisin' }] };
    return payload;
};

// ─── Carte 2 bis : QCM « 3x, pour x = 8, c'est… » (venue de 1108, arbitrage prof 2026-09-12) ─
// La substitution seule, sur un monôme : le premier geste du test (remplacer,
// calculer), l'item DEPP 4e 2023 q.17 réduit au monôme. Familles : les
// chiffres collés (38, 15,4 %), 3 + x (lecture_additive), x³ (puissance) ou
// x × x (carre) quand x^a dépasse 999. x de 2 à 10 ; a × x ≠ a + x ; jamais
// 3x pour x = 4 (la fiche montre 3 × 4 = 12 en passant).

const genSubstitution = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const a = randInt(2, 9);
        const x = randInt(2, 10);
        if ((a === SACRIFIE.a && x === SACRIFIE.x) || a * x === a + x) continue;
        const bon = String(a * x);
        const faux = [
            { valeur: Number(`${a}${x}`), code: 'concatenation' },
            { valeur: a + x, code: 'lecture_additive' },
            x ** a <= 999 ? { valeur: x ** a, code: 'puissance' } : { valeur: x * x, code: 'carre' },
        ];
        if (new Set([a * x, ...faux.map((f) => f.valeur)]).size !== 4) continue;
        const payload = makeQcm([bon, ...faux.map((f) => String(f.valeur))], bon, `<p><b>${tex(`${a}${L}`)}, pour ${tex(L)} = ${x}, c'est…</b></p>`, opts(L_SUBST));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, nommer(faux.map((f) => ({ code: CODE_COMMUN[f.code], valeur: f.valeur })), bon));
        payload._meta.params = { lettre: L, a, x, v: a * x, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const payload = makeQcm(['24', '46', '10', '1296'], '24', `<p><b>${tex('4x')}, pour ${tex('x')} = 6, c'est…</b></p>`, opts(L_SUBST));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, nommer([{ code: CODE_COMMUN.concatenation, valeur: 46 }, { code: CODE_COMMUN.lecture_additive, valeur: 10 }, { code: CODE_COMMUN.puissance, valeur: 1296 }], '24'));
    payload._meta.params = { lettre: 'x', a: 4, x: 6, v: 24, distracteurs: [{ code: 'concatenation' }, { code: 'lecture_additive' }, { code: 'puissance' }] };
    return payload;
};

// ─── Carte 2 : QCM « Laquelle est vraie pour x = 4 ? » ────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 120; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer(['ax_b', 'b_ax']);
        const familles = famillesFausses(it);
        const codes = new Set(familles.map((f) => f.code));
        // trois familles distinctes quand la forme les porte, sinon un voisin complète
        const faux = [...codes].map((code) => familles.find((f) => f.code === code)).slice(0, 3);
        while (faux.length < 3) {
            const voisin = it.v + randomChoice([-3, -2, -1, 1, 2, 3]);
            if (voisin >= 0 && ![it.v, ...faux.map((f) => f.valeur)].includes(voisin)) faux.push({ valeur: voisin, code: 'voisin' });
        }
        if (new Set([it.v, ...faux.map((f) => f.valeur)]).size !== 4) continue;
        const egalite = (d) => tex(`${ecriture(it.e, L)} = ${d}`);
        const bon = egalite(it.v);
        const payload = makeQcm([bon, ...faux.map((f) => egalite(f.valeur))], bon, `<p><b>Pour ${tex(L)} = ${it.x}, une seule égalité est vraie. Laquelle ?</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        const nommables = nommablesItem(it);
        ranger(payload, faux.filter((f) => nommables.some((n) => n.code === CODE_COMMUN[f.code] && cleReponse(n.valeur) === cleReponse(f.valeur)))
            .map((f) => ({ code: CODE_COMMUN[f.code], valeur: egalite(f.valeur) })));
        payload._meta.params = { lettre: L, ...it, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const payload = makeQcm([11, 28, 7, 12].map((d) => tex(`2x + 5 = ${d}`)), tex('2x + 5 = 11'), `<p><b>Pour ${tex('x')} = 3, une seule égalité est vraie. Laquelle ?</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, [{ code: CODE_COMMUN.concatenation, valeur: tex('2x + 5 = 28') }, { code: CODE_COMMUN.x_ignore, valeur: tex('2x + 5 = 7') }]);
    payload._meta.params = { lettre: 'x', e: { forme: 'ax_b', a: 2, b: 5, signe: '+' }, x: 3, v: 11, distracteurs: [{ code: 'concatenation' }, { code: 'x_ignore' }, { code: 'voisin' }] };
    return payload;
};

// ─── Carte 3 : drag « je remplace, je calcule, je compare » ───────────────

const genDrag = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const it = tirer(['ax_b', 'ax_b', 'b_ax', 'ax']);
        const vraie = randomChoice([true, false]);
        const familles = famillesFausses(it);
        const droite = vraie ? it.v : (familles.length && randomChoice([true, false]) ? randomChoice(familles).valeur : it.v + randomChoice([-3, -2, -1, 1, 2, 3]));
        if (droite < 0 || (droite === it.v) !== vraie) continue;
        const { e, x, v } = it;
        const remplace = e.forme === 'ax' ? `${e.a} ${FOIS} {{${x}}}`
            : e.forme === 'b_ax' ? `${e.b} + ${e.a} ${FOIS} {{${x}}}`
                : `${e.a} ${FOIS} {{${x}}} ${e.signe} ${e.b}`;
        const phrase = `Pour $${L}$ = ${x}, est-ce que $${ecriture(e, L)} = ${droite}$ ? Je remplace : ${remplace} = {{${v}}}. Réponse : {{${vraie ? 'oui' : 'non'}}}`;
        const candidats = [vraie ? 'non' : 'oui', String(droite), ...familles.map((f) => String(f.valeur)), String(x + 1)];
        const leurres = filtrerDistracteurs(phrase, candidats).slice(0, 3);
        if (leurres.length < 2) continue;
        const payload = makeTexteTrou(phrase, '<p><b>Complète le test.</b></p>', { ...opts(L_DRAG), mode: 'drag', distracteurs: leurres });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, membre_seul: EXPLICATIONS.membre_seul } };
        ranger(payload, erreursDrag(leurres));
        payload._meta.params = { lettre: L, ...it, droite, vraie, distracteurs: leurres.map(() => ({ code: 'membre_seul' })) };
        return payload;
    }
    const phrase = 'Pour $x$ = 3, est-ce que $2x + 5 = 11$ ? Je remplace : 2 × {{3}} + 5 = {{11}}. Réponse : {{oui}}';
    const payload = makeTexteTrou(phrase, '<p><b>Complète le test.</b></p>', { ...opts(L_DRAG), mode: 'drag', distracteurs: ['non', '28', '4'] });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, membre_seul: EXPLICATIONS.membre_seul } };
    ranger(payload, erreursDrag(['non', '28', '4']));
    payload._meta.params = { lettre: 'x', e: { forme: 'ax_b', a: 2, b: 5, signe: '+' }, x: 3, v: 11, droite: 11, vraie: true, distracteurs: [{ code: 'membre_seul' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre égalités au même membre gauche, quatre membres droits distincts ; la clé est la valeur, les trois autres des valeurs de famille (un voisin complète si la forme n\'en porte pas trois)',
        'sacrifié : 3x + 2 pour x = 4, quel que soit le membre droit',
    ],
    qcm_unique: {
        support: 'ax + b, ax − b ou b + ax (a 2..5, b 1..9), x entier 0..6',
        familles: {
            gauche_a_droite: 'b + ax lu (b + a) × x : 2 + 3x pour x = 4 → 20 (DEPP 4e 2023 q.17, 22,6 %), la principale',
            concatenation: '3x lu 34 : 34 + 2 = 36 (15,4 %)',
            x_ignore: 'a + b, le x oublié',
            voisin: 'à ± 1..3 de la clé',
        },
    },
    vf_tableau_vf: 'quatre lignes, deux vraies, deux fausses dont une de famille au moins',
    drag: 'trois leurres : l\'autre réponse (oui/non), le membre droit quand il diffère, une valeur de famille, x + 1',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    test_tableau_vf: genVf,
    substitution_qcm_unique: genSubstitution,
    vraie_qcm_unique: genQcm,
    geste_texte_trou_drag: genDrag,
};

export const CATEGORIES = [
    { id: 'test', label: L_VF, formatsList: [{ key: 'test_tableau_vf', label: 'Vrai/Faux' }] },
    { id: 'substitution', label: L_SUBST, formatsList: [{ key: 'substitution_qcm_unique', label: 'QCM' }] },
    { id: 'vraie', label: L_QCM, formatsList: [{ key: 'vraie_qcm_unique', label: 'QCM' }] },
    { id: 'geste', label: L_DRAG, formatsList: [{ key: 'geste_texte_trou_drag', label: 'Glisser-déposer' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
